import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Clock,
  IdentificationBadge,
  MapPin,
  UsersThree,
  LockKey,
  Buildings,
} from "@phosphor-icons/react";
import { meetingService } from "../services/meetingService";
import { placeService } from "../services/placeService";
import { authService } from "../services/authService";
import { accountService } from "../services/accountService";

export default function MeetingDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const me = authService.getCurrentUser();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  async function load() {
    setLoading(true);
    const m = await meetingService.getById(id);
    if (!m) {
      setMeeting(null);
      setLoading(false);
      return;
    }

    let enriched = m;
    if (m.accommodationId) {
      let lat = m.exactLat ?? m.approxLat;
      let lng = m.exactLng ?? m.approxLng;
      if (typeof lat !== "number" || typeof lng !== "number") {
        const saved = await accountService.getSavedLocation();
        lat = saved?.lat;
        lng = saved?.lng;
      }
      if (typeof lat === "number" && typeof lng === "number") {
        const places = await placeService
          .listNearby({ latitude: lat, longitude: lng, radiusKm: 100, forMap: true })
          .catch(() => []);
        enriched = meetingService.enrichWithPlaces([m], places)[0] ?? m;
      }
    }

    setMeeting(enriched);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, [id]);

  async function handleJoin() {
    setJoining(true);
    try {
      await meetingService.join(id);
      toast.success("Te has unido al encuentro.");
      load();
    } catch (err) {
      toast.error(err.message || "No se pudo unir");
    } finally {
      setJoining(false);
    }
  }

  if (loading) {
    return (
      <div className="px-5 pt-6 text-xs uppercase tracking-[0.22em] text-[rgba(255,255,255,0.45)]">
        Cargando...
      </div>
    );
  }
  if (!meeting) {
    return (
      <div className="px-5 pt-6">
        <p className="text-sm text-[rgba(255,255,255,0.7)]">Encuentro no encontrado.</p>
      </div>
    );
  }

  const isMine = meeting.organizerId === me?.id;
  const joined = meeting.participants?.includes(me?.id);
  const accepted = joined && meeting.status === "closed";
  const canSeeExact = isMine || accepted;
  const canJoin = !isMine && !joined && meeting.status === "open";

  return (
    <div className="px-5 pt-6 pb-12" data-testid="meeting-detail-page">
      <div className="mx-auto max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[rgba(255,255,255,0.55)] hover:text-[#f5f5f5]"
          data-testid="detail-back-button"
        >
          <ArrowLeft size={14} weight="thin" /> Volver
        </button>

        <header className="mb-6">
          <p className="zx-label">
            {meeting.type === "2+2" ? "Encuentro · 2+2" : "Encuentro · 1+1"}
          </p>
          <h1 className="font-serif text-4xl tracking-tight md:text-5xl">
            {isMine ? "Tu encuentro" : meeting.organizerNick}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            {meeting.status === "open" ? (
              <span className="zx-tag accent">Abierto</span>
            ) : (
              <span className="zx-tag">Cupo cerrado</span>
            )}
            {joined && <span className="zx-tag accent">Inscrito</span>}
            {isMine && <span className="zx-tag">Mío</span>}
          </div>
        </header>

        <div className="zx-card p-6 space-y-5" data-testid="detail-info-card">
          <Row icon={Clock} label="Cuándo">
            {new Date(meeting.time).toLocaleString("es-ES", {
              dateStyle: "full",
              timeStyle: "short",
            })}
          </Row>
          <Row icon={MapPin} label="Dónde">
            {canSeeExact ? (
              <span data-testid="detail-exact-address">{meeting.address}</span>
            ) : (
              <span className="flex items-center gap-2 text-[rgba(255,255,255,0.55)]">
                <LockKey size={12} weight="thin" /> Visible al confirmar el cupo
              </span>
            )}
          </Row>
          {meeting.placeName && (
            <Row icon={Buildings} label="Hospedaje">
              <span data-testid="detail-accommodation-name">
                {meeting.placeName}
                {meeting.placeLocality ? ` · ${meeting.placeLocality}` : ""}
              </span>
            </Row>
          )}
          <Row icon={IdentificationBadge} label="Identificador">
            {canSeeExact ? (
              <span data-testid="detail-exact-identifier">{meeting.identifier}</span>
            ) : (
              <span className="flex items-center gap-2 text-[rgba(255,255,255,0.55)]">
                <LockKey size={12} weight="thin" /> Se revela al ser aceptado
              </span>
            )}
          </Row>
          <Row icon={UsersThree} label="Cupo">
            {meeting.participants.length} / {meeting.capacity}
          </Row>
        </div>

        <div className="mt-6 zx-card p-6 space-y-4">
          <p className="zx-label">Buscado</p>
          {meeting.physicalAttributes?.length ? (
            <Group title="Físico" tags={meeting.physicalAttributes} />
          ) : null}
          {meeting.preferences?.length ? (
            <Group title="Preferencias" tags={meeting.preferences} />
          ) : null}
          {meeting.interests?.length ? (
            <Group title="Gustos" tags={meeting.interests} accent />
          ) : null}
        </div>

        {canJoin && (
          <button
            className="zx-btn-primary mt-8"
            onClick={handleJoin}
            disabled={joining}
            data-testid="detail-join-button"
          >
            {joining ? "Procesando..." : "Solicitar entrada"}
          </button>
        )}

        {joined && !accepted && (
          <div className="mt-8 zx-card p-5 text-center text-sm text-[rgba(255,255,255,0.7)]">
            Tu solicitud está pendiente de cierre del cupo. Cuando se cierre, verás dirección e identificador.
          </div>
        )}
        {accepted && (
          <div className="mt-8 zx-card p-5 text-center text-sm text-[#a9e3b3]">
            Tu participación está confirmada. La dirección y el identificador ya son visibles arriba.
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-2 text-[rgba(255,255,255,0.55)]">
        <Icon size={14} weight="thin" />
        <span className="text-[10px] uppercase tracking-[0.22em]">{label}</span>
      </div>
      <div className="text-right text-sm text-[#f5f5f5]">{children}</div>
    </div>
  );
}

function Group({ title, tags, accent = false }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[rgba(255,255,255,0.45)]">
        {title}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span key={t} className={`zx-tag ${accent ? "accent" : ""}`}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}
